import { useMemo } from 'react';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { aud } from '@/utils/format';
import { artistOutstanding, dayPayouts } from '@/domain/payoutFlow';
import type { Artist } from '@/domain/types';

interface Props {
  artist: Artist;
  /** The sim clock. Today's envelope and the age of anything owed read off this. */
  dayIso: string;
}

const dayLabel = (dayIso: string): string =>
  format(new Date(`${dayIso}T12:00:00+10:00`), 'EEE d MMM');

/**
 * What the artist sees about their own money: TODAY, and anything still owed.
 * Replaces the monthly payout ledger — the month-by-month register is the
 * business's record, not the artist's screen (client, 30 Jul).
 *
 * Two rules shape what is on show:
 *  - No commission percentage and no ticket price. The artist sees their own pay
 *    and a session count; what the client was charged and how the split works are
 *    not their screen's business. (This is why nothing here reads `takingsAud` or
 *    `arrangementLabel`.)
 *  - Their pay is never part of a day. A day the takings could not cover appears
 *    below, whole, until a payment can carry it — so "outstanding" is always a
 *    list of whole days, never a remainder.
 *
 * READ, DON'T RECOMPUTE: today's envelope comes from `dayPayouts` and the arrears
 * from `artistOutstanding`, the same reads the manager's payout card uses, so the
 * two screens can never disagree about what this artist is owed.
 */
export default function ArtistEarningsDay({ artist, dayIso }: Props) {
  // Subscribe so a sign-off or a deferral re-renders both blocks.
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const bookings = useBrainStore((s) => s.bookings);

  const today = useMemo(
    () => dayPayouts(artist.studioId, dayIso).find((p) => p.artistId === artist.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artist.id, artist.studioId, dayIso, bookings, payoutRecords],
  );
  const outstanding = useMemo(
    () => artistOutstanding(artist.studioId, artist.id, dayIso).filter((d) => d.dayIso < dayIso.slice(0, 10)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artist.id, artist.studioId, dayIso, payoutRecords],
  );

  const owedAud = outstanding.reduce((sum, d) => sum + d.envelopeAud, 0);
  const anyEscalated = outstanding.some((d) => d.escalated);

  // Today's state, in the artist's terms. Nothing here mentions cash cover: whether
  // the day can afford the envelope is the studio's problem, not theirs.
  const state = ((): { label: string; pill: string } => {
    if (today?.record?.status === 'signed') return { label: 'Paid today', pill: 'pill-mint' };
    if (today?.record?.status === 'sent') return { label: 'Sent to confirm', pill: 'pill-gold' };
    if (today?.record?.status === 'deferred') return { label: 'Waiting to be paid', pill: 'pill-blood' };
    if (!today) return { label: 'No sessions today', pill: 'pill-cream' };
    if (!today.payable) return { label: 'In chair', pill: 'pill-gold' };
    if (!today.saleSettled) return { label: 'Sale pending', pill: 'pill-cream' };
    return { label: 'Payable now', pill: 'pill-gold' };
  })();

  return (
    <div className="space-y-4" data-testid="artist-earnings-day">
      <div className="panel-dark p-4 border border-gold-dim/25" data-testid="earnings-today">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="label-small-caps text-gold-bright">Today · {dayLabel(dayIso.slice(0, 10))}</p>
            <p className="heading-serif text-2xl text-cream mt-1.5">{aud(today?.envelopeAud ?? 0)}</p>
            <p className="font-serif text-cream-soft text-xs mt-1">
              {today
                ? `${today.sessionCount} session${today.sessionCount === 1 ? '' : 's'}`
                : 'Nothing booked in your chair today.'}
            </p>
          </div>
          <span className={`text-[10px] ${state.pill}`} data-testid="earnings-today-state">
            {state.label}
          </span>
        </div>
        {today && today.record?.status !== 'signed' && today.record?.status !== 'sent' ? (
          <p
            className="font-serif text-cream-faint text-xs mt-3 pt-3 border-t border-gold-dim/15"
            data-testid="earnings-nothing-to-sign"
          >
            Nothing to sign yet. Your studio manager hands the envelope over when the cash is ready, and it
            appears here to confirm.
          </p>
        ) : null}
      </div>

      <div className="panel p-4" data-testid="earnings-outstanding">
        <div className="flex items-center justify-between gap-3">
          <p className="label-small-caps text-gold-bright">Outstanding</p>
          {outstanding.length > 0 ? (
            <span className={`text-[10px] ${anyEscalated ? 'pill-blood' : 'pill-cream'}`}>
              {outstanding.length} day{outstanding.length === 1 ? '' : 's'} waiting
            </span>
          ) : null}
        </div>

        {outstanding.length === 0 ? (
          <p className="font-serif text-cream-soft text-sm mt-2" data-testid="earnings-nothing-outstanding">
            Nothing outstanding. You are paid up to today.
          </p>
        ) : (
          <>
            <table className="w-full text-sm mt-3">
              <tbody>
                {outstanding.map((d) => (
                  <tr
                    key={d.dayIso}
                    className="border-b border-gold-dim/10"
                    data-testid={`earnings-outstanding-${d.dayIso}`}
                  >
                    <td className="py-2 pr-3 font-mono text-cream">{dayLabel(d.dayIso)}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[10px] ${d.escalated ? 'pill-blood' : 'pill-cream'}`}>
                        Owed {d.ageDays} day{d.ageDays === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono text-cream">{aud(d.envelopeAud)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="pt-2.5 pr-3 font-serif text-cream" colSpan={2}>
                    Total owed to you
                  </td>
                  <td className="pt-2.5 text-right font-mono text-cream font-semibold" data-testid="earnings-owed-total">
                    {aud(owedAud)}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="font-serif text-cream-soft text-xs mt-3">
              You are never paid part of a day. When the day&rsquo;s takings could not cover your envelope it
              waits here, whole, and goes out with the next payment that covers it.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
